import React, { useEffect, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import useDownloadPdf from "../../../hooks/useDownloadPdf";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../../ui/dialog";
import { toast } from "sonner";
import { FileText, BookOpen, Loader2, RefreshCw, MessageSquarePlus, Download, Printer, FileDown, Receipt, Send, Edit2, AlertTriangle } from "lucide-react";
import DownloadsTab from "./DownloadsTab";
import InvoicesTab from "./InvoicesTab";
import DocumentCopyLogsTab from "./DocumentCopyLogsTab";
import PackingListDocumentView from "./PackingListDocumentView";
import BookingFormDocumentView from "./BookingFormDocumentView";
import DocumentEditDialog from "./DocumentEditDialog";
import LifecycleReviewQueueTab from "./LifecycleReviewQueueTab";
import LifecycleStatusBadge from "./LifecycleStatusBadge";
import DocumentHubTab from "./DocumentHubTab";

const PACKING_STATUSES = [
  { value: "nieuw", label: "Nieuw" },
  { value: "in_behandeling", label: "In behandeling" },
  { value: "afgehandeld", label: "Afgehandeld" },
  { value: "geannuleerd", label: "Geannuleerd" },
];
const BOOKING_STATUSES = [
  { value: "nieuw", label: "Nieuw" },
  { value: "in_behandeling", label: "In behandeling" },
  { value: "geboekt", label: "Geboekt" },
  { value: "afgewezen", label: "Afgewezen" },
  { value: "geannuleerd", label: "Geannuleerd" },
];

function StatusPill({ status }) {
  const map = {
    nieuw: "bg-blue-100 text-blue-800",
    in_behandeling: "bg-amber-100 text-amber-800",
    afgehandeld: "bg-green-100 text-green-800",
    geboekt: "bg-green-100 text-green-800",
    afgewezen: "bg-red-100 text-red-800",
    geannuleerd: "bg-gray-100 text-gray-700",
  };
  return <span className={`text-[10px] uppercase tracking-wider font-chivo font-bold px-2 py-0.5 ${map[status] || "bg-gray-100"}`}>{status}</span>;
}

function makeListTab(opts) {
  // opts: { endpoint, statuses, kind, columns, renderDetail }
  return function ListTab() {
    const { user } = useAuth();
    // P0 hotfix — medewerker mag pakbon/boekingsformulier LEZEN/PRINTEN
    // maar NIET bewerken of e-mailen naar klant. Verberg deze knoppen
    // voor medewerker zodat de UI niet schijnbaar de actie aanbiedt om
    // vervolgens een 403-toast te tonen.
    const canMutateDocs = ["owner", "admin", "manager", "webshop_manager"].includes(user?.role);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    // Fase 2B — Lifecycle filter. Default 'actief' toont alleen geldige documenten.
    const [lifecycleFilter, setLifecycleFilter] = useState("actief");
    const [detail, setDetail] = useState(null);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);
    const [emailForm, setEmailForm] = useState({ to: "", cc: "", subject: "", message: "", include_pdf: true });
    const [editOpen, setEditOpen] = useState(false);  // Stage A — bewerk-dialog
    // Phase 18.7 — shared PDF download UX (loading, slow-hint, toasts, de-dupe).
    const pdfDl = useDownloadPdf();

    const refresh = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (statusFilter) params.set("status", statusFilter);
        if (search) params.set("q", search);
        const r = await adminApi.get(`${opts.endpoint}?${params.toString()}`);
        setItems(r.data || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [statusFilter]);

    // P0 hotfix — deeplink-ondersteuning. Wanneer de URL `?pakbon={id}`
    // of `?boeking={id}` bevat (zoals gebruikt vanuit BestellingDetail
    // gekoppelde documenten), laad dat specifieke document en open de
    // detail-dialog automatisch. Werkt voor zowel manager+ als medewerker
    // (medewerker heeft sinds P0 hotfix leesrechten op deze endpoints).
    useEffect(() => {
      try {
        const params = new URLSearchParams(window.location.search);
        // Iteratie A — accepteer ook `booking_id` (canonical vanuit
        // entity-search / deep-link) naast legacy `boeking` / `pakbon`.
        // Query-param blijft BEHOUDEN zodat F5-refresh detail heropent.
        const targetId = opts.kind === "packing"
          ? params.get("pakbon")
          : opts.kind === "booking"
            ? (params.get("booking_id") || params.get("boeking"))
            : null;
        if (!targetId) return;
        let cancelled = false;
        (async () => {
          try {
            const r = await adminApi.get(`${opts.endpoint}/${targetId}`);
            if (cancelled) return;
            setDetail(r.data);
          } catch (e) {
            toast.error(formatApiError(e));
          }
        })();
        return () => { cancelled = true; };
      } catch (_) { /* noop */ }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateStatus = async (id, status) => {
      setBusy(true);
      try {
        await adminApi.put(`${opts.endpoint}/${id}/status`, { status });
        setItems((arr) => arr.map((it) => (it.id === id ? { ...it, status } : it)));
        if (detail?.id === id) setDetail({ ...detail, status });
        toast.success(`Status: ${status}`);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { setBusy(false); }
    };

    const addNote = async () => {
      if (!detail || !note.trim()) return;
      setBusy(true);
      try {
        const r = await adminApi.post(`${opts.endpoint}/${detail.id}/notes`, { note });
        setDetail({ ...detail, notes: [...(detail.notes || []), r.data.note] });
        setNote("");
        toast.success("Notitie toegevoegd");
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { setBusy(false); }
    };

    const downloadPdf = async () => {
      if (!detail || !opts.pdfEndpoint) return;
      const docLabel = opts.kind === "packing" ? "Paklijst"
                      : opts.kind === "booking" ? "Boekingsformulier"
                      : "Document";
      await pdfDl.download(
        async () => {
          const r = await adminApi.get(`${opts.pdfEndpoint(detail.id)}`, { responseType: "blob" });
          return {
            blob: new Blob([r.data], { type: "application/pdf" }),
            filename: `${opts.kind}-${(detail.id || "").slice(0, 8)}.pdf`,
          };
        },
        {
          loadingMessage: `${docLabel} wordt gegenereerd…`,
          successMessage: `${docLabel} klaar — download gestart.`,
          errorMessage: "PDF kon niet worden gedownload. Probeer het opnieuw.",
          taskId: `${opts.kind}-pdf-${detail.id}`,
        },
      );
    };

    const printDoc = async () => {
      // PRINT via the generated PDF — this avoids @media print issues inside
      // the Radix Dialog (portal + transform pushed the document off-screen,
      // resulting in a blank print). The PDF is the canonical document anyway:
      // same layout, same letterhead, identical to what the customer receives.
      if (!detail) return;
      if (!opts.pdfEndpoint) {
        window.print();
        return;
      }
      setBusy(true);
      try {
        const r = await adminApi.get(`${opts.pdfEndpoint(detail.id)}`, { responseType: "blob" });
        const blob = new Blob([r.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const win = window.open(url, "_blank", "noopener,noreferrer");
        if (win) {
          // Best-effort: trigger native print dialog once the PDF viewer loaded
          try {
            win.addEventListener("load", () => {
              try { win.focus(); win.print(); } catch (_) { /* user prints manually */ }
            });
          } catch (_) { /* same-origin fallback */ }
        } else {
          toast.error("Pop-ups geblokkeerd — sta pop-ups toe om het document te printen.");
        }
        setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { setBusy(false); }
    };

    const openEmailDialog = () => {
      if (!detail) return;
      const fullName = [detail.first_name, detail.last_name].filter(Boolean).join(" ").trim() || "klant";
      const idShort = (detail.id || "").slice(0, 8).toUpperCase();
      const kindLabel = opts.kind === "packing" ? "Paklijst" : "Boekingsformulier";
      const subject = `${kindLabel} 5999Cargo - ${fullName || idShort}`;
      const message =
        `Geachte ${fullName},\n\n` +
        `Bijgaand ontvangt u ${opts.kind === "packing" ? "de paklijst" : "het boekingsformulier"} die via 5999Cargo is aangemaakt.\n\n` +
        "Controleer de gegevens zorgvuldig. Als er iets niet klopt, neem dan contact met ons op voordat de zending wordt verwerkt.\n\n" +
        "Met vriendelijke groet,\n5999Cargo Group Europe";
      setEmailForm({
        to: detail.email || "",
        cc: "",
        subject,
        message,
        include_pdf: true,
      });
      setEmailOpen(true);
    };

    const sendEmail = async () => {
      if (!detail) return;
      if (!emailForm.to || !emailForm.to.includes("@")) {
        toast.error("Geen geldig e-mailadres ingevuld.");
        return;
      }
      setBusy(true);
      try {
        const payload = {
          to: emailForm.to.trim(),
          cc: emailForm.cc
            ? emailForm.cc.split(/[,;]/).map((s) => s.trim()).filter((s) => s.includes("@"))
            : [],
          subject: emailForm.subject,
          message: emailForm.message,
          include_pdf: emailForm.include_pdf,
        };
        const r = await adminApi.post(`${opts.endpoint}/${detail.id}/email`, payload);
        const status = r.data?.status;
        if (status === "sent") {
          toast.success(`E-mail verstuurd naar ${r.data.to}${r.data.attachment ? " (met PDF-bijlage)" : ""}`);
        } else if (status === "skipped_no_key") {
          toast.warning("RESEND_API_KEY ontbreekt — e-mail is niet verstuurd. Stel de key in via Instellingen → Productieconfiguratie.");
        } else if (status === "skipped_no_recipient") {
          toast.error("Geen ontvanger opgegeven.");
        } else {
          toast.error(`E-mail mislukt: ${r.data?.error || status || "onbekende fout"}`);
        }
        setEmailOpen(false);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { setBusy(false); }
    };

    // Fase 2B — Lifecycle-filter (client-side). Standaard 'actief' verbergt
    // duplicaat/geannuleerd/vervangen/concept. Legacy documenten zonder
    // lifecycle_status vallen onder de 'actief'-default (backend-normalize).
    const filteredItems = items.filter((it) => {
      if (lifecycleFilter === "all") return true;
      const lc = it.lifecycle_status || "actief"; // legacy = actief
      if (lifecycleFilter === "review") {
        return !!it.lifecycle_review_required;
      }
      return lc === lifecycleFilter;
    });

    return (
      <div className="space-y-4" data-testid={`docs-${opts.kind}-tab`}>
        <div className="flex flex-wrap items-center gap-3">
          <Input data-testid={`docs-${opts.kind}-search`} placeholder="Zoek..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && refresh()} className="rounded-none max-w-xs" />
          <select data-testid={`docs-${opts.kind}-status-filter`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-none border border-gray-300 bg-white px-3 py-2 text-sm h-10">
            <option value="">Alle statussen</option>
            {opts.statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select data-testid={`docs-${opts.kind}-lifecycle-filter`} value={lifecycleFilter} onChange={(e) => setLifecycleFilter(e.target.value)} className="rounded-none border border-gray-300 bg-white px-3 py-2 text-sm h-10" title="Lifecycle-filter">
            <option value="actief">Actief</option>
            <option value="all">Alles</option>
            <option value="concept">Concept</option>
            <option value="duplicaat">Duplicaat</option>
            <option value="geannuleerd">Geannuleerd</option>
            <option value="vervangen">Vervangen (historisch)</option>
            <option value="review">Lifecycle controle nodig</option>
          </select>
          <Button onClick={refresh} variant="outline" className="rounded-none" data-testid={`docs-${opts.kind}-refresh`}><RefreshCw className="w-4 h-4 mr-2" />Verversen</Button>
          <div className="ml-auto text-xs text-gray-500">{filteredItems.length} van {items.length} items</div>
        </div>

        <div className="bg-white border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] uppercase tracking-wider font-chivo font-bold text-gray-600">
              <tr>
                {opts.columns.map((c) => <th key={c.key} className="px-3 py-2 text-left">{c.label}</th>)}
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={opts.columns.length + 1} className="p-6 text-center text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Laden...</td></tr>
              ) : filteredItems.length === 0 ? (
                <tr><td colSpan={opts.columns.length + 1} className="p-6 text-center text-gray-500">Geen inzendingen{lifecycleFilter !== "all" ? ` met filter '${lifecycleFilter}'` : ""}.</td></tr>
              ) : filteredItems.map((it) => (
                <tr key={it.id} data-testid={`docs-${opts.kind}-row-${it.id}`} className="hover:bg-gray-50">
                  {opts.columns.map((c) => (
                    <td key={c.key} className="px-3 py-2">
                      {c.key === "status" ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          {c.render ? c.render(it) : it[c.key]}
                          {(it.lifecycle_status && it.lifecycle_status !== "actief") && (
                            <LifecycleStatusBadge status={it.lifecycle_status} size="sm" />
                          )}
                          {it.lifecycle_review_required && (
                            <span className="inline-flex items-center gap-1 border border-amber-500 bg-amber-50 text-amber-800 text-[10px] font-bold px-1.5 py-0.5">
                              Controle nodig
                            </span>
                          )}
                        </div>
                      ) : (
                        c.render ? c.render(it) : it[c.key]
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" className="rounded-none" onClick={() => { setDetail(it); setNote(""); }} data-testid={`docs-${opts.kind}-open-${it.id}`}>Open</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent
            className={opts.DocumentComponent ? "max-w-5xl pkl-no-print" : "max-w-2xl"}
            data-testid={`docs-${opts.kind}-detail`}
          >
            <DialogHeader>
              <DialogTitle>{detail?.email}</DialogTitle>
              <DialogDescription>{detail?.created_at && new Date(detail.created_at).toLocaleString("nl-NL")}</DialogDescription>
            </DialogHeader>
            {detail && (
              <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Top action bar */}
                {(opts.pdfEndpoint || opts.DocumentComponent) && (
                  <div className="pkl-actions flex flex-wrap items-center gap-2 pb-2 border-b border-gray-100">
                    {opts.pdfEndpoint && (
                      <Button
                        size="sm"
                        className="rounded-none bg-[#082c50] hover:bg-[#1a5193] text-white"
                        onClick={downloadPdf}
                        disabled={busy || pdfDl.loading}
                        data-testid={`docs-${opts.kind}-pdf`}
                      >
                        {pdfDl.loading
                          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> PDF wordt gegenereerd...</>
                          : <><FileDown className="w-4 h-4 mr-2" /> PDF downloaden</>}
                      </Button>
                    )}
                    {pdfDl.loading && pdfDl.isSlow && (
                      <span className="text-xs text-gray-500" data-testid={`docs-${opts.kind}-pdf-slow`}>
                        {pdfDl.slowHint}
                      </span>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-none"
                      onClick={printDoc}
                      data-testid={`docs-${opts.kind}-print`}
                    >
                      <Printer className="w-4 h-4 mr-2" /> Afdrukken
                    </Button>
                    {(opts.kind === "packing" || opts.kind === "booking") && canMutateDocs && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none"
                        onClick={() => setEditOpen(true)}
                        disabled={busy}
                        data-testid={`docs-${opts.kind}-edit`}
                      >
                        <Edit2 className="w-4 h-4 mr-2" /> Bewerken
                      </Button>
                    )}
                    {(opts.kind === "packing" || opts.kind === "booking") && canMutateDocs && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none"
                        onClick={openEmailDialog}
                        disabled={busy}
                        data-testid={`docs-${opts.kind}-mail`}
                      >
                        <MessageSquarePlus className="w-4 h-4 mr-2" /> E-mailen naar klant
                      </Button>
                    )}
                    <div className="ml-auto" />
                  </div>
                )}
                {opts.DocumentComponent ? (
                  <opts.DocumentComponent pkl={detail} />
                ) : (
                  opts.renderDetail(detail)
                )}
                {(detail.updated_at || detail.revision) && (
                  <div
                    className="pkl-no-print text-xs bg-blue-50 border border-blue-200 px-3 py-2 flex items-center gap-2"
                    data-testid={`docs-${opts.kind}-history`}
                  >
                    <Edit2 className="w-3 h-3 text-blue-700 flex-shrink-0" />
                    <span className="text-gray-700">
                      Laatst gewijzigd op{" "}
                      <strong>{new Date(detail.updated_at || detail.created_at).toLocaleString("nl-NL")}</strong>
                      {detail.updated_by_email && <> door <strong>{detail.updated_by_email}</strong></>}
                      {detail.revision != null && detail.revision > 0 && <> · revisie #{detail.revision}</>}
                      {detail.last_edit_reason && <> · reden: <em>{detail.last_edit_reason}</em></>}
                    </span>
                  </div>
                )}
                <div className="pkl-no-print">
                  <Label className="text-[10px] uppercase">Status</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {opts.statuses.map((s) => (
                      <Button key={s.value} size="sm" variant={detail.status === s.value ? "default" : "outline"} className="rounded-none" onClick={() => updateStatus(detail.id, s.value)} disabled={busy}>{s.label}</Button>
                    ))}
                  </div>
                </div>
                <div className="pkl-no-print">
                  <Label className="text-[10px] uppercase">Interne notities</Label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                    {(detail.notes || []).length === 0 ? (
                      <div className="text-xs text-gray-400">Nog geen notities.</div>
                    ) : (detail.notes || []).map((n) => (
                      <div key={n.id} className="bg-gray-50 border-l-2 border-[#0055FF] px-3 py-2 text-xs">
                        <div className="text-gray-500">{n.author_email} · {new Date(n.created_at).toLocaleString("nl-NL")}</div>
                        <div className="text-gray-800 whitespace-pre-wrap mt-1">{n.body}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none" placeholder="Notitie..." />
                    <Button onClick={addNote} disabled={busy || !note.trim()} className="rounded-none"><MessageSquarePlus className="w-4 h-4 mr-1" />Bewaar</Button>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="pkl-no-print">
              <Button variant="outline" className="rounded-none" onClick={() => setDetail(null)}>Sluiten</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Email composer dialog (paklijst + boekingsformulier) */}
        <Dialog open={emailOpen} onOpenChange={(o) => { if (!busy) setEmailOpen(o); }}>
          <DialogContent className="max-w-2xl" data-testid={`docs-${opts.kind}-email-dialog`}>
            <DialogHeader>
              <DialogTitle>
                {opts.kind === "packing" ? "Paklijst e-mailen naar klant" : "Boekingsformulier e-mailen naar klant"}
              </DialogTitle>
              <DialogDescription>
                De PDF wordt automatisch als bijlage meegestuurd (inclusief briefpapier indien actief).
                Interne notities komen nooit in de e-mail of bijlage.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Aan</Label>
                <Input
                  data-testid={`docs-${opts.kind}-email-to`}
                  className="rounded-none"
                  value={emailForm.to}
                  onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                  placeholder="klant@voorbeeld.nl"
                />
              </div>
              <div>
                <Label className="text-xs">CC (optioneel, komma-gescheiden)</Label>
                <Input
                  data-testid={`docs-${opts.kind}-email-cc`}
                  className="rounded-none"
                  value={emailForm.cc}
                  onChange={(e) => setEmailForm({ ...emailForm, cc: e.target.value })}
                  placeholder="cc@5999cargo.nl"
                />
              </div>
              <div>
                <Label className="text-xs">Onderwerp</Label>
                <Input
                  data-testid={`docs-${opts.kind}-email-subject`}
                  className="rounded-none"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Bericht</Label>
                <Textarea
                  data-testid={`docs-${opts.kind}-email-message`}
                  className="rounded-none"
                  rows={8}
                  value={emailForm.message}
                  onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={emailForm.include_pdf}
                  onChange={(e) => setEmailForm({ ...emailForm, include_pdf: e.target.checked })}
                  data-testid={`docs-${opts.kind}-email-include-pdf`}
                />
                PDF meesturen ({opts.kind === "packing" ? "paklijst" : "boekingsformulier"}-{(detail?.id || "").slice(0, 8)}.pdf)
              </label>
            </div>
            <DialogFooter>
              <Button variant="ghost" className="rounded-none" onClick={() => setEmailOpen(false)} disabled={busy}>
                Annuleren
              </Button>
              <Button
                onClick={sendEmail}
                disabled={busy || !emailForm.to || !emailForm.subject.trim() || !emailForm.message.trim()}
                data-testid={`docs-${opts.kind}-email-send`}
                className="rounded-none bg-[#082c50] hover:bg-[#1a5193] text-white"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquarePlus className="w-4 h-4 mr-2" />}
                Verstuur e-mail
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Stage A — Document bewerken dialog (paklijst + boekingsformulier) */}
        {detail && (opts.kind === "packing" || opts.kind === "booking") && (
          <DocumentEditDialog
            open={editOpen}
            onOpenChange={setEditOpen}
            docType={opts.kind === "packing" ? "packing_list" : "booking_form"}
            doc={detail}
            onSaved={(updated) => {
              // Update detail + lijst met nieuwe data
              setDetail((prev) => ({ ...prev, ...updated }));
              setItems((arr) => arr.map((it) => (it.id === updated.id ? { ...it, ...updated } : it)));
            }}
          />
        )}
      </div>
    );
  };
}

const PackingTab = makeListTab({
  endpoint: "/admin/packing-lists",
  pdfEndpoint: (id) => `/admin/packing-lists/${id}/pdf`,
  DocumentComponent: PackingListDocumentView,
  statuses: PACKING_STATUSES,
  kind: "packing",
  columns: [
    { key: "first_name", label: "Klant", render: (it) => <><div>{it.first_name} {it.last_name}</div><div className="text-xs text-gray-500">{it.email}</div></> },
    { key: "destination", label: "Bestemming" },
    { key: "item_count", label: "Items", render: (it) => <>{it.item_count} (€ {(it.total_value || 0).toFixed(2)})</> },
    { key: "status", label: "Status", render: (it) => <StatusPill status={it.status} /> },
    { key: "created_at", label: "Datum", render: (it) => <span className="text-xs text-gray-500">{new Date(it.created_at).toLocaleString("nl-NL")}</span> },
  ],
});

const BookingTab = makeListTab({
  endpoint: "/admin/booking-forms",
  pdfEndpoint: (id) => `/admin/booking-forms/${id}/pdf`,
  DocumentComponent: BookingFormDocumentView,
  statuses: BOOKING_STATUSES,
  kind: "booking",
  columns: [
    { key: "first_name", label: "Klant", render: (it) => <><div>{it.first_name} {it.last_name}</div><div className="text-xs text-gray-500">{it.email}</div></> },
    { key: "destination", label: "Bestemming" },
    { key: "shipment_type", label: "Type" },
    { key: "method", label: "Methode", render: (it) => it.method || "—" },
    { key: "status", label: "Status", render: (it) => <StatusPill status={it.status} /> },
    { key: "created_at", label: "Datum", render: (it) => <span className="text-xs text-gray-500">{new Date(it.created_at).toLocaleString("nl-NL")}</span> },
  ],
});

export default function DocumentenSection() {
  const { hasPermission, user } = useAuth();
  // Fase 2B — Lifecycle review-queue is voor Manager+ (medewerker mag niet classificeren)
  const canViewLifecycleReview = ["owner", "admin", "manager"].includes(user?.role);
  const [reviewCount, setReviewCount] = useState(0);

  // Snelle count-lookup zodat we een badge op de tab kunnen tonen
  useEffect(() => {
    if (!canViewLifecycleReview) return;
    (async () => {
      try {
        const r = await adminApi.get("/admin/lifecycle/review-queue");
        setReviewCount(r.data?.count || 0);
      } catch {
        setReviewCount(0);
      }
    })();
  }, [canViewLifecycleReview]);

  if (!hasPermission("customers:view")) {
    return <div className="bg-white border border-gray-200 p-10 text-center" data-testid="docs-no-access"><h2 className="font-chivo font-black text-xl text-[#0A192F]">Geen toegang</h2></div>;
  }
  // Mirror backend RBAC: invoices list is gated to owner/admin/manager/content_manager.
  // Hide the tab entirely for medewerker so we don't surface a "Geen toegang" toast.
  const canViewInvoices = ["owner", "admin", "manager", "content_manager"].includes(user?.role);
  return (
    <div className="space-y-4" data-testid="documenten-section">
      <h1 className="font-chivo font-black text-2xl text-[#0A192F] flex items-center gap-2">
        <FileText className="w-6 h-6 text-[#0055FF]" /> Documenten &amp; Formulieren
      </h1>
      <Tabs defaultValue={reviewCount > 0 && canViewLifecycleReview ? "lifecycle-review" : "hub"}>
        {/* Mobile: horizontaal scrollbare tabs zodat alle 4-5 tabs bereikbaar
            blijven op iPhone Safari (anders verdwijnen 'Gegenereerde
            facturen' en 'Documentkopieën' uit beeld). Op desktop gedraagt
            de tabbar zich identiek aan voorheen. */}
        <div className="-mx-2 sm:mx-0 overflow-x-auto scrollbar-none">
          <TabsList className="rounded-none flex-nowrap whitespace-nowrap inline-flex w-max sm:w-auto px-2 sm:px-0">
            <TabsTrigger value="hub" data-testid="docs-tab-hub" className="whitespace-nowrap"><FileText className="w-4 h-4 mr-2" /> Alle documenten</TabsTrigger>
            <TabsTrigger value="packing" data-testid="docs-tab-packing" className="whitespace-nowrap"><FileText className="w-4 h-4 mr-2" /> Paklijsten</TabsTrigger>
            <TabsTrigger value="booking" data-testid="docs-tab-booking" className="whitespace-nowrap"><BookOpen className="w-4 h-4 mr-2" /> Boekingsformulieren</TabsTrigger>
            {canViewInvoices && (
              <TabsTrigger value="invoices" data-testid="docs-tab-invoices" className="whitespace-nowrap"><Receipt className="w-4 h-4 mr-2" /> Gegenereerde facturen</TabsTrigger>
            )}
            {canViewInvoices && (
              <TabsTrigger value="copy-logs" data-testid="docs-tab-copy-logs" className="whitespace-nowrap"><Send className="w-4 h-4 mr-2" /> Documentkopieën</TabsTrigger>
            )}
            <TabsTrigger value="downloads" data-testid="docs-tab-downloads" className="whitespace-nowrap"><Download className="w-4 h-4 mr-2" /> Downloads beheer</TabsTrigger>
            {canViewLifecycleReview && (
              <TabsTrigger
                value="lifecycle-review"
                data-testid="docs-tab-lifecycle-review"
                className="whitespace-nowrap"
              >
                <AlertTriangle className={`w-4 h-4 mr-2 ${reviewCount > 0 ? "text-amber-600" : ""}`} />
                Lifecycle controle
                {reviewCount > 0 && (
                  <span
                    className="ml-1 inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold h-4 min-w-[16px] px-1"
                    data-testid="docs-tab-lifecycle-review-badge"
                  >
                    {reviewCount}
                  </span>
                )}
              </TabsTrigger>
            )}
          </TabsList>
        </div>
        <TabsContent value="hub" className="mt-4"><DocumentHubTab /></TabsContent>
        <TabsContent value="packing" className="mt-4"><PackingTab /></TabsContent>
        <TabsContent value="booking" className="mt-4"><BookingTab /></TabsContent>
        {canViewInvoices && (
          <TabsContent value="invoices" className="mt-4"><InvoicesTab /></TabsContent>
        )}
        {canViewInvoices && (
          <TabsContent value="copy-logs" className="mt-4"><DocumentCopyLogsTab /></TabsContent>
        )}
        <TabsContent value="downloads" className="mt-4"><DownloadsTab /></TabsContent>
        {canViewLifecycleReview && (
          <TabsContent value="lifecycle-review" className="mt-4">
            <LifecycleReviewQueueTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
